import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Section from "src/components/Section"
import Media from "src/components/Media"
import Grid from "src/components/Grid"
import { colors, animations, globals, mq } from "src/styles"
import TextLockup from "src/components/TextLockup"
// import ResponsiveComponent from 'src/components/ResponsiveComponent'
import { headerHeight } from "src/components/Header"
import { use100vh } from "react-div-100vh"
import ShapeTop from "src/assets/images/logoShapeTop.svg"
import ShapeRight from "src/assets/images/logoShapeRight.svg"
import ShapeBottom from "src/assets/images/logoShapeBottom.svg"
import ShapeLeft from "src/assets/images/logoShapeLeft.svg"

const Wrapper = styled(Section)`
  --text-color: ${colors.white};
  --light-text-color: ${colors.white};
  position: relative;
  border-radius: var(--card-border-radius);
  overflow: hidden;
  ${({ isFirstSection }) =>
    !isFirstSection
      ? `
    margin-top: calc(var(--site-margins) / 1);
  `
      : `
    margin-top: 0 !important;
    margin-bottom: calc(var(--site-margins) / 2);
  `}
  ${({ overlayTextColor }) =>
    overlayTextColor === "dark"
      ? `
    color: ${colors.textColor};
  `
      : `
    color: ${colors.bgColor};
  `}
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => hideMobile && "display: none"}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => hideDesktop && "display: none"}
  }
`

const WideMediaWrap = styled.div`
  ${({ height }) =>
    height !== "auto" &&
    `
    position: relative;
    z-index: 2;
  `}
`

const ContentWrap = styled.div`
  position: relative;
`

const MediaItem = styled(Media)`
  z-index: 2;
  width: 100%;
  overflow: hidden;
  ${({ isFirstSection }) =>
    isFirstSection
      ? css`
          opacity: 0;
          will-change: opacity;
          transform: translateZ(0);
          animation: ${animations.fadeIn} ${animations.mediumSpeed} 0.1s
            ease-in-out forwards;
        `
      : ""}
  ${({ overlay, height }) =>
    overlay && height !== "auto"
      ? `
    position: absolute !important;
    top: 0;
    left: 0;
    height: 100%;
  `
      : `
    position: relative;
  `}
  ${({ height }) =>
    height !== "auto"
      ? `
    height: 100%;
    min-height: ${height};
    > div,
    > div > div {
      height: 100% !important;
      min-height: ${height};
      position: relative;
    }
    img {
      position: absolute;
      top: 0;
      left: 0;
      object-fit: cover;
      width: 100%;
      height: 100%;
      min-height: ${height};
      border-radius: ${globals.cardBorderRadius};
      overflow: hidden;
    }
    video {
      position: absolute;
      top: 0;
      left: 0;
      object-fit: cover;
      width: 100%;
      height: 100%;
      min-height: ${height};
      border-radius: ${globals.cardBorderRadius};
      overflow: hidden;
    }
  `
      : `
    height: 100%;
  `}
`

const OverlayContent = styled.div`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  display: flex;
  flex-direction: column;
  ${({ height }) =>
    height === "auto"
      ? `
    position: absolute;
  `
      : `
    position: relative;
  `}
  ${({ height }) =>
    height !== "auto" &&
    `
    min-height: ${height};
  `}
  justify-content: ${({ verticalPlacement }) => {
    if (!verticalPlacement) return "center"
    if (verticalPlacement === "top") return "flex-start"
    if (verticalPlacement === "bottom") return "flex-end"
    else return verticalPlacement
  }};
  text-align: ${({ overlayTextAlignment }) => overlayTextAlignment || "left"};
  h1, h2, h3 {
    max-width: 10em;
  }
  h4, h5 {
    max-width: 12em;
  }
  h6 {
    max-width: 16em;
  }
  p {
    max-width: 36em;
  }
`

const OverlaySection = styled(Section)`
  position: relative;
  ${({ isFirstSection }) =>
    isFirstSection &&
    `
    ${headerHeight("padding-top")}
  `}
`

function getHorizontalPlacementGridValues({
  fullWidth,
  overlayPlacementHorizontal,
}) {
  if (!fullWidth) {
    return {
      left: {
        medium: "[10] 2",
        large: "[8] 3",
        larger: "[6] 6",
      },
      center: {
        medium: "1 [10] 1",
        large: "2 [8] 2",
        larger: "3 [6] 3",
      },
      right: {
        medium: "6 [5] 1",
        large: "6 [5] 1",
        larger: "6 [5] 1",
      },
    }[overlayPlacementHorizontal]
  } else {
    return {
      left: {
        medium: "[1]",
        large: "[1]",
        larger: "[1]",
      },
      center: {
        medium: "1 [10] 1",
        large: "1 [10] 1",
        larger: "1 [10] 1",
      },
      right: {
        medium: "6 [6]",
        large: "6 [6]",
        larger: "6 [6]",
      },
    }[overlayPlacementHorizontal]
  }
}

const WideMedia = ({
  media,
  mediaMedium,
  mediaSmall,
  width,
  theme,
  spacing,
  hideMobile,
  hideDesktop,

  prevTheme,
  nextTheme,
  height,
  text,
  paragraphSize,
  overlay,
  actions,
  overlayPlacementVertical,
  overlayPlacementHorizontal,
  alignment,
  isFirstSection,
  overlayTextColor,
  id,
}) => {
  const winHeight = use100vh()
  if (!media) {
    return false
  }

  if (!height) {
    height = "auto"
  }

  const fullWidth = true

  const fullHeight = !isFirstSection ? winHeight : false

  const heightValues = {
    auto: "auto",
    fullHeight: fullHeight && isFirstSection ? fullHeight + "px" : "100vh",
    mediumHeight: "70vh",
    shortHeight: "50vh",
  }

  const overlayGridSettings = getHorizontalPlacementGridValues({
    fullWidth,
    overlayPlacementHorizontal,
  })
  const hasOverlay = text?._rawText !== null || actions.length > 0

  const renderMedia = (mediaItem, size, hasOverlay, autoHeight) => {
    if (mediaItem) {
      return (
        <>
          {overlay && overlay !== "none" && (
            <div
              css={css`
                position: absolute;
                z-index: 3;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(
                  0,
                  0,
                  0,
                  ${overlay === "medium" ? 0.15 : 0.25}
                );
              `}
            />
          )}
          <MediaItem
            rounded
            overlay={hasOverlay}
            media={media}
            playing={true}
            isFirstSection={isFirstSection}
            loop={true}
            height={heightValues[height]}
            posterImage={mediaItem.posterImage}
            autoPlay={true}
            overlayTextColor={overlayTextColor}
            loading={isFirstSection ? "eager" : "lazy"}
            altText={text?.eyebrow || media.originalFilename}
          />
        </>
      )
    }
    return false
  }

  return (
    <Grid small="container" large="m [1] m">
      <Wrapper
        nextTheme={nextTheme}
        prevTheme={prevTheme}
        setTheme={theme}
        padded={!fullWidth}
        fullWidth
        isFirstSection={isFirstSection}
        overlayTextColor={overlayTextColor}
        id={id}
        vertical={spacing?.vertical}
        horizontal={spacing?.horizontal}
        hideMobile={hideMobile}
        hideDesktop={hideDesktop}
      >
        <WideMediaWrap
          height={heightValues[height]}
          overlayTextColor={overlayTextColor}
        >
          <Grid
            small={fullWidth ? "[1]" : "container"}
            medium={fullWidth ? "[1]" : "container"}
            large={fullWidth ? "[1]" : "container"}
            larger={fullWidth ? "[1]" : "container"}
          >
            <ContentWrap>
              {/* <ResponsiveComponent
                small={renderMedia(mediaSmall, 'small', hasOverlay, height === 'auto')}
                medium={renderMedia(mediaMedium, 'medium', hasOverlay, height === 'auto')}
                large={renderMedia(media, 'large', hasOverlay, height === 'auto')}
              /> */}

              <div>
                {renderMedia(media, "large", hasOverlay, height === "auto")}
              </div>
              {hasOverlay && (
                <OverlayContent
                  padded={!fullWidth}
                  overlayTextAlignment={alignment}
                  verticalPlacement={overlayPlacementVertical}
                  height={heightValues[height]}
                >
                  <OverlaySection isFirstSection={isFirstSection}>
                    <Grid small="1 [12] 1">
                      <Grid small="[1]" {...overlayGridSettings}>
                        <TextLockup
                          eyebrow={text?.eyebrow}
                          text={text?._rawText}
                          newStyle={text?.newStyle}
                          textSize={paragraphSize}
                          actions={actions}
                          alignment={alignment}
                          transitionIn={!isFirstSection}
                        />
                      </Grid>
                    </Grid>
                  </OverlaySection>
                </OverlayContent>
              )}
            </ContentWrap>
          </Grid>
        </WideMediaWrap>
      </Wrapper>
    </Grid>
  )
}

WideMedia.defaultProps = {
  width: "margins",
  height: "auto",
  overlayTextColor: "light",
}

export default WideMedia
